import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { Hub } from 'aws-amplify/utils';
import {
  signUp,
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  confirmSignUp,
  resendSignUpCode,
  signInWithRedirect
} from 'aws-amplify/auth';
import axios from 'axios';
import {
  markGoogleSignInPending,
  clearGoogleSignInPending,
  wasGoogleSignInPending,
  resolveAuthErrorMessage,
  googleSignInAbandonedMessage,
  invalidProfileAfterSignInMessage,
  stripOAuthQueryFromPath,
  loadStoredAuthError,
  storeAuthError,
  hasOAuthCallbackParams
} from '../utils/authErrors';

/** Wait for Amplify OAuth redirect to finish (success or failure). */
function waitForOAuthHubEvent(timeoutMs = 12000) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsubscribe();
      resolve(result);
    };

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') finish('signedIn');
      if (payload.event === 'signInWithRedirect_failure') finish('failure');
    });

    const timer = setTimeout(() => finish('timeout'), timeoutMs);
  });
}

export const AuthContext = createContext();

export function HandleUser() {
  const [authed, setAuthed] = useState(false);
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [onboarding, setOnboarding] = useState(true);
  const [authError, setAuthError] = useState(() => loadStoredAuthError());
  const [authReady, setAuthReady] = useState(false);
  const authCheckDone = useRef(false);
  const oauthJustCompleted = useRef(false);
  const authedRef = useRef(false);

  const persistAuthError = useCallback((message) => {
    if (!message) return;
    setAuthError(message);
    storeAuthError(message);
  }, []);

  const clearAuthError = useCallback(() => {
    setAuthError('');
    storeAuthError('');
  }, []);

  const restoreAuthErrorFromStorage = useCallback(() => {
    const stored = loadStoredAuthError();
    if (stored) setAuthError(stored);
    return stored;
  }, []);

  const reportAuthError = useCallback((error, searchParams = null) => {
    const message = resolveAuthErrorMessage({ error, searchParams });
    if (message) {
      persistAuthError(message);
      clearGoogleSignInPending();
    }
    return message;
  }, [persistAuthError]);

  /** Restore Cognito session (used on load and after background token refresh). */
  const restoreSession = useCallback(async ({ forceRefresh = false } = {}) => {
    await fetchAuthSession({ forceRefresh });
    const session = await fetchAuthSession();
    if (!session.tokens?.idToken) {
      throw new Error('Not authenticated');
    }
    const currentUser = await getCurrentUser();
    setUser(currentUser);
    setAuthed(true);
    authedRef.current = true;
    return currentUser;
  }, []);

  useEffect(() => {
    authedRef.current = authed;
  }, [authed]);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      setAuthed(false);
      authedRef.current = false;
      setUser(null);
      setUserProfile(null);
    } catch (error) {
      console.log('error signing out: ', error);
    }
  }, []);

  const refreshUserProfile = useCallback(async () => {
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response = await axios.get('https://api.amplifyloyalty.com/app/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      if (data && data.user && (data.store || data.needsOnboarding)) {
        setUserProfile(data);
        oauthJustCompleted.current = false;
        clearGoogleSignInPending();
        return data;
      }
      console.error('Invalid user profile received:', data);
      if (oauthJustCompleted.current || wasGoogleSignInPending()) {
        persistAuthError(invalidProfileAfterSignInMessage());
      }
      oauthJustCompleted.current = false;
      clearGoogleSignInPending();
      await handleSignOut();
      return null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      if (oauthJustCompleted.current || wasGoogleSignInPending()) {
        persistAuthError(
          resolveAuthErrorMessage({ error }) || googleSignInAbandonedMessage()
        );
      }
      oauthJustCompleted.current = false;
      clearGoogleSignInPending();
      await handleSignOut();
      return null;
    }
  }, [handleSignOut, persistAuthError]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlError = params.get('error') || params.get('error_description');
    if (urlError) {
      reportAuthError(null, params);
      const cleanSearch = stripOAuthQueryFromPath(window.location.search, { keepErrors: true });
      const cleanUrl = `${window.location.pathname}${cleanSearch || ''}${window.location.hash || ''}`;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, [reportAuthError]);

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect_failure') {
        reportAuthError(payload.data?.error);
      }
      if (
        payload.event === 'signedIn' ||
        payload.event === 'tokenRefresh'
      ) {
        clearGoogleSignInPending();
        clearAuthError();
        if (authCheckDone.current && !authedRef.current) {
          restoreSession().catch(() => {});
        }
      }
    });
    return () => unsubscribe();
  }, [reportAuthError, clearAuthError, restoreSession]);

  useEffect(() => {
    const checkUser = async () => {
      setAuthReady(false);
      const search = window.location.search;
      const params = new URLSearchParams(search);
      const oauthCallback = hasOAuthCallbackParams(search);

      try {
        if (oauthCallback) {
          const hubResult = await waitForOAuthHubEvent();
          if (hubResult === 'failure') {
            await signOut().catch(() => {});
            throw new Error('Google sign-in could not be completed.');
          }
          if (hubResult === 'timeout') {
            const session = await fetchAuthSession({ forceRefresh: true });
            if (!session.tokens?.idToken) {
              await signOut().catch(() => {});
              throw new Error('Google sign-in did not finish. Please try again.');
            }
          }
        }

        try {
          await restoreSession({ forceRefresh: oauthCallback });
        } catch (firstErr) {
          if (oauthCallback) throw firstErr;
          await restoreSession({ forceRefresh: true });
        }

        if (oauthCallback) oauthJustCompleted.current = true;
        clearAuthError();

        if (oauthCallback) {
          const cleanSearch = stripOAuthQueryFromPath(search);
          const cleanUrl = `${window.location.pathname}${cleanSearch || ''}${window.location.hash || ''}`;
          window.history.replaceState({}, document.title, cleanUrl);
        }
      } catch (err) {
        setUser(null);
        setAuthed(false);
        authedRef.current = false;
        if (oauthCallback) {
          await signOut().catch(() => {});
        }

        if (params.get('error') || params.get('error_description')) {
          reportAuthError(err, params);
        } else if (oauthCallback) {
          const message =
            resolveAuthErrorMessage({ error: err }) || googleSignInAbandonedMessage();
          persistAuthError(message);
          clearGoogleSignInPending();
          const cleanSearch = stripOAuthQueryFromPath(search);
          const cleanUrl = `${window.location.pathname}${cleanSearch || ''}${window.location.hash || ''}`;
          window.history.replaceState({}, document.title, cleanUrl);
        } else if (wasGoogleSignInPending()) {
          persistAuthError(googleSignInAbandonedMessage());
          clearGoogleSignInPending();
        }
      } finally {
        authCheckDone.current = true;
        setAuthReady(true);
      }
    };
    checkUser();
  }, [reportAuthError, clearAuthError, persistAuthError, restoreSession]);

  // Hub may fire after checkUser; re-read stored error once auth check finishes
  useEffect(() => {
    if (!authCheckDone.current) return;
    if (!authError) restoreAuthErrorFromStorage();
  }, [authError, restoreAuthErrorFromStorage]);

  useEffect(() => {
    if (authed && user) {
      refreshUserProfile();
    } else {
      setUserProfile(null);
    }
  }, [authed, user, refreshUserProfile]);

  useEffect(() => {
    if (userProfile?.needsOnboarding) {
      setOnboarding(false);
      return;
    }
    if (userProfile?.store?.onboarding) {
      const statuses = Object.values(userProfile.store.onboarding);
      setOnboarding(!statuses.includes(false));
    } else {
      setOnboarding(true);
    }
  }, [userProfile]);

  const setUserProfileFromResponse = useCallback((data) => {
    setUserProfile(data);
  }, []);

  const membershipRole = userProfile?.membership?.role || null;
  const canWrite = membershipRole === 'owner' || membershipRole === 'admin';
  const isOwner = membershipRole === 'owner';
  const needsStoreOnboarding = Boolean(userProfile?.needsOnboarding);

  const handleSignUp = async (email, password, userAttributes) => {
    try {
      const { user: newUser } = await signUp({
        username: email,
        password,
        options: { userAttributes }
      });
      return newUser;
    } catch (error) {
      console.log('error signing up:', error);
      return Promise.reject(error);
    }
  };

  const handleConfirmSignUp = async (username, code) => {
    try {
      await confirmSignUp({ username, confirmationCode: code });
    } catch (error) {
      console.log('error confirming sign up', error);
      throw error;
    }
  };

  const handleResendSignUpCode = async (username) => {
    try {
      await resendSignUpCode({ username });
    } catch (err) {
      console.log('error resending code: ', err);
      throw err;
    }
  };

  const handleSignIn = async (email, password) => {
    try {
      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password,
        options: { authFlowType: 'USER_PASSWORD_AUTH' }
      });
      if (isSignedIn) {
        const currentUser = await getCurrentUser();
        setUser(currentUser);
        setAuthed(true);
      } else {
        const { signInDetails } = nextStep;
        if (signInDetails) {
          return { isSignedIn, nextStep, user: { username: email } };
        }
      }
      return { isSignedIn, nextStep };
    } catch (error) {
      console.log('error signing in', error);
      return Promise.reject(error);
    }
  };

  const handleSignInWithGoogle = async () => {
    clearAuthError();
    markGoogleSignInPending();
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch (error) {
      clearGoogleSignInPending();
      const message = reportAuthError(error);
      return Promise.reject(new Error(message || 'Google sign-in failed.'));
    }
  };

  const handleForgotPassword = async (username) => {
    try {
      return await resetPassword({ username });
    } catch (error) {
      console.log('error resetting password: ', error);
      throw error;
    }
  };

  const handleConfirmForgotPassword = async (username, confirmationCode, newPassword) => {
    try {
      await confirmResetPassword({ username, confirmationCode, newPassword });
    } catch (error) {
      console.log('error confirming new password: ', error);
      throw error;
    }
  };

  const handleCompleteNewPassword = async (newPassword) => {
    try {
      await confirmSignIn({ challengeResponse: newPassword });
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      setAuthed(true);
    } catch (error) {
      console.log('error completing new password: ', error);
      return Promise.reject(error);
    }
  };

  const getJwtToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString();
    } catch {
      return null;
    }
  }, []);

  return {
    authed,
    user,
    handleSignUp,
    handleSignIn,
    handleSignInWithGoogle,
    handleSignOut,
    getJwtToken,
    handleCompleteNewPassword,
    handleForgotPassword,
    handleConfirmForgotPassword,
    handleConfirmSignUp,
    handleResendSignUpCode,
    userProfile,
    setUserProfileFromResponse,
    refreshUserProfile,
    onboarding,
    membershipRole,
    canWrite,
    isOwner,
    needsStoreOnboarding,
    authError,
    authReady,
    clearAuthError,
    restoreAuthErrorFromStorage,
    reportAuthError
  };
}

export function AuthContextProvider({ children }) {
  const authHandler = HandleUser();

  return <AuthContext.Provider value={authHandler}>{children}</AuthContext.Provider>;
}
